import {
  SIDENAV_COLLAPSE_THRESHOLD,
  SIDENAV_COLLAPSED_WIDTH,
} from '@abb-procure/constants';
import { computed, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { pxToRem } from '../utilities/px-to-rem.util';

export interface SidenavOptions {
  cssVarName?: string;
  placement?: 'left' | 'right';
  clampedMinWidth?: number;
  clampedMaxWidth?: number;
  defaultWidth?: number;
}

export type SidePanelTab = 'insights' | 'copilot' | 'documents' | 'parsedData';

/**
 * Global service to request opening a specific side panel tab.
 * This is separate from SidenavService to allow cross-component communication.
 */
@Injectable({
  providedIn: 'root',
})
export class SidePanelTabService {
  private readonly _openTabRequest$ = new Subject<SidePanelTab>();
  readonly openTabRequest$ = this._openTabRequest$.asObservable();

  requestOpenTab(tab: SidePanelTab): void {
    this._openTabRequest$.next(tab);
  }
}

@Injectable({
  providedIn: 'root',
})
export class SidenavService {
  cssVarName = '--procure-sidenav-width';
  placement: 'left' | 'right' = 'right';
  clampedMinWidth = 20;
  clampedMaxWidth = 60;
  defaultWidth: undefined | number = undefined;

  readonly width = signal<number>(SIDENAV_COLLAPSED_WIDTH);
  readonly clampedWidth = signal<number>(SIDENAV_COLLAPSED_WIDTH);

  readonly collapsedWidth = SIDENAV_COLLAPSED_WIDTH;

  readonly isExpanded = computed(() => {
    return this.width() > SIDENAV_COLLAPSE_THRESHOLD;
  });

  setSideNavOptions(options: SidenavOptions): void {
    if (options.cssVarName) {
      this.cssVarName = options.cssVarName;
    }
    if (options.placement) {
      this.placement = options.placement;
    }
    if (options.clampedMinWidth) {
      this.clampedMinWidth = options.clampedMinWidth;
    }
    if (options.clampedMaxWidth) {
      this.clampedMaxWidth = options.clampedMaxWidth;
    }
    if (options.defaultWidth) {
      this.defaultWidth = options.defaultWidth;
    }
  }

  setCssVarName(cssVarName: string): void {
    this.cssVarName = cssVarName;
  }

  resizeSidenav(
    startingCursorX: number,
    currentCursorX: number,
    startingWidth: number,
  ): void {
    const cursorDeltaX = pxToRem(currentCursorX) - startingCursorX;
    const newWidth =
      this.placement === 'right'
        ? startingWidth - cursorDeltaX
        : startingWidth + cursorDeltaX;
    this.setSidenavWidth(newWidth);
  }

  toggleExpansion(): void {
    if (this.isExpanded()) {
      this.setCollapsedWidth();
    } else {
      this.setBaseWidth();
    }
  }

  setSidenavWidth(width: number): void {
    const maxWidth = Math.min(
      pxToRem(window.innerWidth * 0.5),
      this.clampedMaxWidth,
    );

    const clampedWidth = Math.min(
      Math.max(width, this.clampedMinWidth),
      maxWidth,
    );

    document.body.style.setProperty(this.cssVarName, `${clampedWidth}rem`);

    this.width.set(width);
    this.clampedWidth.set(clampedWidth);
  }

  setCollapsedWidth(): void {
    this.setSidenavWidth(SIDENAV_COLLAPSED_WIDTH);
  }

  setBaseWidth(): void {
    if (this.defaultWidth) {
      this.setSidenavWidth(this.defaultWidth);
    } else {
      const defaultWidth = pxToRem(window.innerWidth * 0.3);
      this.setSidenavWidth(defaultWidth);
    }
  }
}
